import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getEstadoColor, getSubEstadoColor } from '../../constants/reservaColors';
import { useReservaFilters } from '../../hooks/useReservaFilters';
import {
  Box,
  Text,
  Button,
  Grid,
  GridItem,
  useToast,
  Flex,
  Badge,
  VStack,
  HStack,
  Divider,
  Tooltip,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Collapse,
  Checkbox,
  IconButton,
} from '@chakra-ui/react';
import Calendar from 'react-calendar'; // Asegúrate de instalar react-calendar: npm install react-calendar
import 'react-calendar/dist/Calendar.css';

const AdminInhabiles = () => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date()); // Fecha para vista semanal
  const [selectedInhabilDate, setSelectedInhabilDate] = useState(new Date()); // Fecha para edición de inhábiles
  const [selectedHours, setSelectedHours] = useState([]);
  const [inhabiles, setInhabiles] = useState({});
  const [currentMonthData, setCurrentMonthData] = useState({});
  const [showJsonSection] = useState(false); // Estado para controlar la visibilidad
  const [calendarViewDate, setCalendarViewDate] = useState(new Date()); // Estado para la vista actual del calendario
  const [weekReservas, setWeekReservas] = useState([]); // Reservas de la semana
  const [expandedDay, setExpandedDay] = useState(null); // null o índice del día expandido (0-6)
  const [mostrarInhabiles, setMostrarInhabiles] = useState(true); // Mostrar u ocultar periodos inhábiles
  const [currentTime, setCurrentTime] = useState(new Date()); // Hora actual para la línea roja
  const [mostrarFiltrosEstados, setMostrarFiltrosEstados] = useState(true); // Mostrar u ocultar filtros de estado
  const [isCalendarOpen, setIsCalendarOpen] = useState(false); // Controlar card colapsable del calendario
  const [isInhabilesEditorOpen, setIsInhabilesEditorOpen] = useState(false); // Controlar card colapsable de edición de inhábiles
  const [isWeekViewOpen, setIsWeekViewOpen] = useState(true); // Controlar card colapsable de vista semanal
  const [refreshCountdown, setRefreshCountdown] = useState(60); // Contador para auto-refresco
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true); // Activar/desactivar auto-refresco
  const fetchWeekReservasRef = useRef(null);
  const loadWeekInhabilesRef = useRef(null);

  // Hook de filtros con vinculación por color
  const {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado
  } = useReservaFilters(
    {
      'activo': true,
      'inactivo': true,
      'cancelado': true
    },
    {
      'bolsa': true,
      'asignada_espera': true,
      'en_curso': true,
      'completado_sin_confirmar': true,
      'pendiente_devolucion': true,
      'pendiente_pago': true,
      'finalizado': true,
      'fallido': true,
      'cancelado_cliente': true,
      'cancelado_administrador': true,
      'cancelado_automatico': true
    }
  );
  const toast = useToast();

  const fetchMonthData = async (month, year) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL; // Usar la variable de entorno
    try {
      const response = await fetch(`${baseURL}/api/mes_inhabil/${month}/${year}/`);
      if (response.ok) {
        const data = await response.json();
        console.log(`Datos cargados para ${month}/${year}:`, data.datos);
        setCurrentMonthData(data.datos || {});
        // Hacer merge de los datos en lugar de reemplazar
        setInhabiles(prev => {
          const merged = {
            ...prev,
            ...(data.datos || {})
          };
          console.log('Estado inhabiles después de merge:', merged);
          return merged;
        });
      } else {
        // No limpiar todo si falla, solo no hacer nada
        console.warn(`No hay datos para ${month}/${year}`);
      }
    } catch (error) {
      console.error("Error al cargar los datos del mes:", error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      // Obtener la semana actual
      const today = new Date();
      const { monday, sunday } = getWeekDates(today);
      
      // Obtener los meses únicos de la semana
      const monthsToLoad = new Set();
      monthsToLoad.add(`${monday.getMonth() + 1}-${monday.getFullYear()}`);
      monthsToLoad.add(`${sunday.getMonth() + 1}-${sunday.getFullYear()}`);
      
      // Cargar datos de todos los meses de la semana
      for (const monthYear of monthsToLoad) {
        const [month, year] = monthYear.split('-').map(Number);
        await fetchMonthData(month, year);
      }
    };
    
    loadInitialData();
  }, []);

  // Cargar las horas del día actual cuando se cargan los datos
  useEffect(() => {
    if (selectedInhabilDate && Object.keys(inhabiles).length > 0) {
      const formattedDate = new Intl.DateTimeFormat('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedInhabilDate);
      setSelectedHours(inhabiles[formattedDate] || []);
    }
  }, [inhabiles, selectedInhabilDate]);

  // Cargar reservas de la semana cuando cambia la fecha seleccionada
  useEffect(() => {
    if (selectedDate) {
      fetchWeekReservasRef.current?.(selectedDate);
    }
  }, [selectedDate]);

  // Cargar datos del mes cuando cambia la vista del calendario
  useEffect(() => {
    const currentMonth = calendarViewDate.getMonth() + 1;
    const currentYear = calendarViewDate.getFullYear();
    fetchMonthData(currentMonth, currentYear);
  }, [calendarViewDate]);

  // Actualizar la hora actual cada minuto para la línea roja
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto
    
    return () => clearInterval(interval);
  }, []);

  // Auto-refresco cada 60 segundos
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          // Cuando llega a 0, refrescar y resetear
          if (selectedDate) {
            fetchWeekReservasRef.current?.(selectedDate);
            loadWeekInhabilesRef.current?.(selectedDate);
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, selectedDate]);

  // Función para obtener el inicio y fin de semana
  const getWeekDates = (date) => {
    const currentDate = new Date(date);
    const dayOfWeek = currentDate.getDay(); // 0 = domingo, 1 = lunes, etc.
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Ajustar para que lunes sea el primer día
    
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return { monday, sunday };
  };

  // Función para obtener reservas de la semana
  const fetchWeekReservas = async (date) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('auth-token');
    
    try {
      const response = await fetch(`${baseURL}/api/reservas/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar reservas');
      }
      
      const data = await response.json();
      
      // Filtrar reservas de la semana
      const { monday, sunday } = getWeekDates(date);
      
      // Debug: mostrar el rango de fechas
      console.log('Rango de semana:', {
        monday: monday.toISOString(),
        sunday: sunday.toISOString(),
        totalReservas: data.length
      });
      
      // Debug: mostrar TODOS los campos de la primera reserva para ver qué está disponible
      if (data.length > 0) {
        console.log('Campos disponibles en la primera reserva:', Object.keys(data[0]));
        console.log('Primera reserva completa:', data[0]);
      }
      
      // Debug: mostrar las primeras reservas sin filtrar (con manejo de errores)
      console.log('Primeras 5 reservas (sin filtrar):', data.slice(0, 5).map(r => {
        try {
          const fecha = new Date(r.fecha_hora_inicio);
          return {
            id: r.id?.substring(0, 8),
            fecha_hora_inicio: r.fecha_hora_inicio,
            fecha_parsed: isNaN(fecha.getTime()) ? 'FECHA INVALIDA' : fecha.toISOString()
          };
        } catch (e) {
          return {
            id: r.id?.substring(0, 8),
            fecha_hora_inicio: r.fecha_hora_inicio,
            error: e.message
          };
        }
      }));
      
      const weekReservas = data.filter(reserva => {
        if (!reserva.fecha_hora_inicio) return false;
        
        const fechaRecogida = new Date(reserva.fecha_hora_inicio);
        
        // Verificar que la fecha sea válida
        if (isNaN(fechaRecogida.getTime())) {
          console.warn('Fecha inválida encontrada:', reserva.id, reserva.fecha_recogida);
          return false;
        }
        
        // Normalizar a solo fechas usando UTC (sin tiempo) para comparación
        const fechaRecogidaSoloFecha = new Date(Date.UTC(
          fechaRecogida.getFullYear(), 
          fechaRecogida.getMonth(), 
          fechaRecogida.getDate()
        ));
        const mondaySoloFecha = new Date(Date.UTC(
          monday.getFullYear(), 
          monday.getMonth(), 
          monday.getDate()
        ));
        const sundaySoloFecha = new Date(Date.UTC(
          sunday.getFullYear(), 
          sunday.getMonth(), 
          sunday.getDate()
        ));
        
        const enRango = fechaRecogidaSoloFecha >= mondaySoloFecha && fechaRecogidaSoloFecha <= sundaySoloFecha;
        
        return enRango;
      });
      
      console.log('Reservas filtradas para la semana:', weekReservas.length);
      setWeekReservas(weekReservas);
    } catch (error) {
      console.error('Error al cargar reservas de la semana:', error);
      setWeekReservas([]);
    }
  };

  // Función para obtener el color según el estado
  const getColorForEstado = (estado) => {
    return getEstadoColor(estado);
  };

  // Función para obtener el color según el sub_estado
  const getColorForSubEstado = (subEstado) => {
    return getSubEstadoColor(subEstado);
  };

  // Función para agrupar reservas por día
  const groupReservasByDay = () => {
    const { monday } = getWeekDates(selectedDate);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(monday);
      currentDay.setDate(monday.getDate() + i);
      
      const dayReservas = weekReservas.filter(reserva => {
        const fechaRecogida = new Date(reserva.fecha_hora_inicio);
        return fechaRecogida.getDate() === currentDay.getDate() &&
               fechaRecogida.getMonth() === currentDay.getMonth() &&
               fechaRecogida.getFullYear() === currentDay.getFullYear();
      });
      
      days.push({
        date: currentDay,
        reservas: dayReservas
      });
    }
    
    return days;
  };

  const getDayName = (dayIndex) => {
    const dayNames = [
      'admin_inhabiles_lunes',
      'admin_inhabiles_martes',
      'admin_inhabiles_miercoles',
      'admin_inhabiles_jueves',
      'admin_inhabiles_viernes',
      'admin_inhabiles_sabado',
      'admin_inhabiles_domingo'
    ];
    return t(dayNames[dayIndex]);
  };

  // Filtrar reservas según filtros activos
  const getReservasFiltradas = () => {
    return weekReservas.filter(reserva => {
      const estadoActivo = estadosActivos[reserva.estado];
      const subEstadoActivo = subEstadosActivos[reserva.sub_estado];
      return estadoActivo && subEstadoActivo;
    });
  };

  // Función para cargar periodos inhábiles de una semana
  const loadWeekInhabiles = async (date) => {
    const { monday, sunday } = getWeekDates(date);
    
    // Obtener los meses únicos de la semana
    const monthsToLoad = new Set();
    monthsToLoad.add(`${monday.getMonth() + 1}-${monday.getFullYear()}`);
    monthsToLoad.add(`${sunday.getMonth() + 1}-${sunday.getFullYear()}`);
    
    // Cargar datos de todos los meses de la semana
    for (const monthYear of monthsToLoad) {
      const [month, year] = monthYear.split('-').map(Number);
      await fetchMonthData(month, year);
    }
  };

  fetchWeekReservasRef.current = fetchWeekReservas;
  loadWeekInhabilesRef.current = loadWeekInhabiles;

  // Función para ir a la semana anterior
  const goToPreviousWeek = async () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
    setExpandedDay(null); // Colapsar al cambiar de semana
    await loadWeekInhabiles(newDate); // Cargar periodos inhábiles de la nueva semana
  };

  // Función para ir a la semana siguiente
  const goToNextWeek = async () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
    setExpandedDay(null); // Colapsar al cambiar de semana
    await loadWeekInhabiles(newDate); // Cargar periodos inhábiles de la nueva semana
  };

  // Función para organizar reservas sin solapamientos en carriles
  const organizarReservasEnCarriles = (reservas) => {
    if (!reservas || reservas.length === 0) return { reservasConCarril: [], totalCarriles: 1 };
    
    // Ordenar por hora de inicio
    const reservasOrdenadas = [...reservas].sort((a, b) => 
      new Date(a.fecha_hora_inicio) - new Date(b.fecha_hora_inicio)
    );
    
    const carriles = [];
    const reservasConCarril = [];
    
    reservasOrdenadas.forEach(reserva => {
      const inicio = new Date(reserva.fecha_hora_inicio);
      const fin = new Date(reserva.fecha_hora_fin);
      
      // Buscar el primer carril disponible
      let carrilIndex = 0;
      let encontrado = false;
      
      while (!encontrado) {
        if (!carriles[carrilIndex]) {
          carriles[carrilIndex] = [];
        }
        
        // Verificar si hay solapamiento con reservas en este carril
        const haySolapamiento = carriles[carrilIndex].some(r => {
          const rInicio = new Date(r.fecha_hora_inicio);
          const rFin = new Date(r.fecha_hora_fin);
          return (inicio < rFin && fin > rInicio);
        });
        
        if (!haySolapamiento) {
          carriles[carrilIndex].push(reserva);
          reservasConCarril.push({ ...reserva, carril: carrilIndex });
          encontrado = true;
        } else {
          carrilIndex++;
        }
      }
    });
    
    return { reservasConCarril, totalCarriles: carriles.length };
  };

  const handleDateClick = (date) => {
    const formattedDate = new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);

    // Actualizar la fecha seleccionada para edición
    setSelectedInhabilDate(date);
    setSelectedHours(inhabiles[formattedDate] || []);
    
    // Conmutar el estado de la card de períodos inhábiles
    setIsInhabilesEditorOpen(prev => !prev);
    
    // Actualizar la vista semanal a la semana que contiene este día
    setSelectedDate(date);
    
    // Cargar reservas y periodos inhábiles de la nueva semana
    fetchWeekReservas(date);
    loadWeekInhabiles(date);
  };

  const toggleHourSelection = (hour) => {
    setSelectedHours((prev) =>
      prev.includes(hour)
        ? prev.filter((h) => h !== hour)
        : [...prev, hour]
    );
  };

  const saveInhabiles = () => {
    if (selectedInhabilDate) {
      const formattedDate = new Intl.DateTimeFormat('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedInhabilDate);

      let updatedData;
      if (selectedHours.length === 0) {
        // Si no hay horas seleccionadas, eliminar el día del estado
        // Calcular updatedData ANTES de setState
        updatedData = { ...currentMonthData };
        delete updatedData[formattedDate];

        setInhabiles((prev) => {
          const updated = { ...prev };
          delete updated[formattedDate];
          return updated;
        });

        setCurrentMonthData(updatedData);

        toast({
          title: t('admin_inhabiles_dia_desmarcado'),
          description: t('admin_inhabiles_dia_eliminado', { fecha: formattedDate }),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Actualizar el estado de inhabiles y currentMonthData
        // Calcular updatedData ANTES de setState
        updatedData = {
          ...currentMonthData,
          [formattedDate]: selectedHours,
        };

        setInhabiles((prev) => ({
          ...prev,
          [formattedDate]: selectedHours,
        }));

        setCurrentMonthData(updatedData);

        toast({
          title: t('admin_inhabiles_dia_marcado'),
          description: t('admin_inhabiles_dia_actualizado', { fecha: formattedDate }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // No resetear selectedInhabilDate para mantenerlo seleccionado
      setSelectedHours([]);
      console.log('saveInhabiles retornando:', updatedData);
      return updatedData;
    }
    return null;
  };

  const persistMonthInhabil = async (dataToSave = null) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL; // Usar la variable de entorno

    // Obtener el mes y año de la vista actual del calendario
    const currentMonth = calendarViewDate.getMonth() + 1; // Mes visible en el calendario (1-12)
    const currentYear = calendarViewDate.getFullYear(); // Año visible en el calendario

    // Usar los datos pasados como parámetro o los del estado
    const sourceData = dataToSave || currentMonthData;

    // Filtrar los días para incluir solo los del mes y año actuales
    const filteredData = Object.keys(sourceData)
      .filter((dateStr) => {
        const [, month, year] = dateStr.split('/').map(Number);
        return month === currentMonth && year === currentYear;
      })
      .reduce((acc, dateStr) => {
        acc[dateStr] = sourceData[dateStr];
        return acc;
      }, {});

    console.log('Persistiendo mes inhábil:', { mes: currentMonth, anio: currentYear, datos: filteredData });
    console.log('sourceData usado:', sourceData);

    try {
      const response = await fetch(`${baseURL}/api/mes_inhabil/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mes: currentMonth,
          anio: currentYear,
          datos: filteredData, // Enviar solo los días filtrados
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Recargar el mes que acabamos de persistir para asegurar sincronización
        await fetchMonthData(currentMonth, currentYear);
        toast({
          title: t("admin_inhabiles_mes_persistido"),
          description: t("admin_inhabiles_mes_persistido_desc"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        console.log("Mes persistido:", data);
      } else {
        const errorData = await response.json();
        toast({
          title: t("admin_inhabiles_error_persistir"),
          description: errorData.detail || t("admin_inhabiles_mes_persistido_desc"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t("admin_inhabiles_error_red"),
        description: t("admin_inhabiles_error_red_desc"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error al persistir el mes:", error);
    }
  };

  return (
    <Box p={4} className="admin-page-container">
      {/* Sección superior: Calendario mensual (izq) + Períodos inhábiles (der) */}
      <Flex gap={8} mb={8}>
        {/* Calendario mensual a la izquierda */}
        <Box flex="1">
          <Card bg="green.50" variant="outline" borderColor="gray.700">
            <CardHeader 
              cursor="pointer" 
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
              _hover={{ bg: 'green.100' }}
              borderTopRadius="md"
              borderBottomRadius={isCalendarOpen ? "none" : "md"}
            >
              <HStack justify="space-between">
                <Heading className={isCalendarOpen ? 'collapsible-card-title-expanded' : 'collapsible-card-title-collapsed'}>{t('admin_inhabiles_titulo')}</Heading>
                <Text className={isCalendarOpen ? 'collapsible-card-indicator-expanded' : 'collapsible-card-indicator-collapsed'}>{isCalendarOpen ? '▲' : '▼'}</Text>
              </HStack>
            </CardHeader>
            <Collapse in={isCalendarOpen} animateOpacity>
              <CardBody display="flex" justifyContent="center">
                <Calendar
                  onClickDay={handleDateClick}
                  onActiveStartDateChange={({ activeStartDate }) => setCalendarViewDate(activeStartDate)}
                  tileClassName={({ date }) => {
                    const formattedDate = new Intl.DateTimeFormat('es-ES', {
                      timeZone: 'Europe/Madrid',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).format(date);
                    const isInhabil = !!inhabiles[formattedDate];
                    if (isInhabil) {
                      console.log(`Día inhábil encontrado: ${formattedDate}`, inhabiles[formattedDate]);
                    }
                    return isInhabil ? 'inhabil-day' : '';
                  }}
                />
              </CardBody>
            </Collapse>
          </Card>
        </Box>

        {/* Períodos inhábiles a la derecha */}
        <Box flex="1">
          {selectedInhabilDate && (
            <Card bg="green.50" variant="outline" borderColor="gray.700">
              <CardHeader 
                cursor="pointer" 
                onClick={() => setIsInhabilesEditorOpen(!isInhabilesEditorOpen)}
                _hover={{ bg: 'green.100' }}
                borderTopRadius="md"
                borderBottomRadius={isInhabilesEditorOpen ? "none" : "md"}
              >
                <HStack justify="space-between">
                  <Heading className={isInhabilesEditorOpen ? 'collapsible-card-title-expanded' : 'collapsible-card-title-collapsed'}>
                    Periodos inhábiles para el día {selectedInhabilDate.toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Heading>
                  <Text className={isInhabilesEditorOpen ? 'collapsible-card-indicator-expanded' : 'collapsible-card-indicator-collapsed'}>{isInhabilesEditorOpen ? '▲' : '▼'}</Text>
                </HStack>
              </CardHeader>
              <Collapse in={isInhabilesEditorOpen} animateOpacity>
                <CardBody>
                  <Button
                    mb={4}
                    colorScheme="purple"
                    bg="purple.500"
                    color="white"
                    _hover={{ bg: 'purple.600' }}
                    onClick={() => {
                      if (selectedHours.length === 24) {
                        setSelectedHours([]);
                      } else {
                        setSelectedHours(Array.from({ length: 24 }, (_, i) => i));
                      }
                    }}
                  >
                    {selectedHours.length === 24 ? t('admin_inhabiles_deseleccionar_todo') : t('admin_inhabiles_seleccionar_todo')}
                  </Button>
                  <Grid templateColumns="repeat(4, 1fr)" gap={2} mt={4}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <GridItem key={i}>
                        <Button
                          size="sm"
                          colorScheme={selectedHours.includes(i) ? 'orange' : 'gray'}
                          bg={selectedHours.includes(i) ? 'orange.500' : 'gray.700'}
                          color="white"
                          _hover={{ bg: selectedHours.includes(i) ? 'orange.600' : 'gray.600' }}
                          onClick={() => toggleHourSelection(i)}
                        >
                          {i}:00-{i}:59
                        </Button>
                      </GridItem>
                    ))}
                  </Grid>
                  <Button
                    mt={4}
                    colorScheme="orange"
                    bg="orange.500"
                    color="white"
                    _hover={{ bg: 'orange.600' }}
                    onClick={async () => {
                      const updatedData = saveInhabiles();
                      await persistMonthInhabil(updatedData);
                      // Colapsar períodos inhábiles y expandir calendario
                      setIsInhabilesEditorOpen(false);
                      setIsCalendarOpen(true);
                    }}
                  >
                    {t('admin_inhabiles_guardar')}
                  </Button>
                </CardBody>
              </Collapse>
            </Card>
          )}
        </Box>
      </Flex>

      {/* Vista semanal de reservas */}
      <Card bg="green.50" variant="outline" borderColor="gray.700" mb={8}>
        <CardHeader 
          cursor="pointer" 
          onClick={() => setIsWeekViewOpen(!isWeekViewOpen)}
          _hover={{ bg: 'green.100' }}
          borderTopRadius="md"
          borderBottomRadius={isWeekViewOpen ? "none" : "md"}
        >
          <HStack justify="space-between">
            <Heading className={isWeekViewOpen ? 'collapsible-card-title-expanded' : 'collapsible-card-title-collapsed'}>{t('admin_inhabiles_vista_semanal')}</Heading>
            <Text className={isWeekViewOpen ? 'collapsible-card-indicator-expanded' : 'collapsible-card-indicator-collapsed'}>{isWeekViewOpen ? '▲' : '▼'}</Text>
          </HStack>
        </CardHeader>
        <Collapse in={isWeekViewOpen} animateOpacity>
          <CardBody>
            {/* Cabecera de la vista semanal: controles/info a la izquierda, filtros a la derecha */}
            <Flex justify="space-between" align="start" mb={4} gap={4}>
              {/* Columna izquierda: Controles e información */}
              <VStack align="start" spacing={2}>
                {/* Primera fila: Badge de conteo + Botón de filtros */}
                <HStack spacing={2}>
                  <Badge colorScheme="blue" fontSize="0.9em" px={3} py={1}>
                    {getReservasFiltradas().length} de {weekReservas.length} reservas
                  </Badge>
                  <Button
                    onClick={() => setMostrarFiltrosEstados(!mostrarFiltrosEstados)}
                    colorScheme="blue"
                    size="sm"
                    leftIcon={<span>🔍</span>}
                    className="boton-filtros"
                  >
                    {t('filtros_boton')}
                  </Button>
                </HStack>
                
                {/* Segunda fila: Navegación de semana */}
                <Flex align="center" gap={3}>
                  <Button
                    colorScheme="teal"
                    size="sm"
                    onClick={goToPreviousWeek}
                    className="admin-button-primary"
                  >
                    ←
                  </Button>
                  <Text fontSize="md" color="gray.600" fontWeight="semibold">
                    {t('admin_inhabiles_semana_del')} {getWeekDates(selectedDate).monday.toLocaleDateString()}
                  </Text>
                  <Button
                    colorScheme="teal"
                    size="sm"
                    onClick={goToNextWeek}
                    className="admin-button-primary"
                  >
                    →
                  </Button>
                </Flex>
                
                {/* Tercera fila: Auto-refresco */}
                <HStack spacing={2}>
                  <Tooltip 
                    label={autoRefreshEnabled ? "Desactivar auto refresh" : "Activar auto refresh"}
                    placement="top"
                    hasArrow
                  >
                    <Checkbox
                      isChecked={autoRefreshEnabled}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setAutoRefreshEnabled(newValue);
                        if (newValue) {
                          setRefreshCountdown(60);
                        }
                      }}
                      colorScheme="teal"
                      size="lg"
                    />
                  </Tooltip>
                  <IconButton
                    icon={<span style={{ fontSize: '18px' }}>&nbsp;🔄 ({refreshCountdown})&nbsp;</span>}
                    onClick={() => {
                      if (selectedDate) {
                        fetchWeekReservas(selectedDate);
                        loadWeekInhabiles(selectedDate);
                      }
                      setRefreshCountdown(60);
                    }}
                    colorScheme="teal"
                    size="sm"
                    variant="outline"
                    aria-label="Refrescar vista semanal"
                    title={`Refrescar vista semanal (auto-refresco en ${refreshCountdown}s)`}
                  />
                </HStack>
              </VStack>

              {/* Columna derecha: Filtros */}
              {mostrarFiltrosEstados && (
                <Box>
                  {/* Estados en una fila */}
                  <Flex gap={2} flexWrap="wrap" mb={2}>
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={estadosActivos['activo'] ? '2px solid white' : '2px solid transparent'}
                      opacity={estadosActivos['activo'] ? 1 : 0.4}
                      onClick={() => toggleEstado('activo')}
                    >
                      {t('estado_activo')}
                    </Badge>
                    <Badge 
                      colorScheme="yellow" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={estadosActivos['inactivo'] ? '2px solid white' : '2px solid transparent'}
                      opacity={estadosActivos['inactivo'] ? 1 : 0.4}
                      onClick={() => toggleEstado('inactivo')}
                    >
                      {t('estado_inactivo')}
                    </Badge>
                    <Badge 
                      colorScheme="red" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={estadosActivos['cancelado'] ? '2px solid white' : '2px solid transparent'}
                      opacity={estadosActivos['cancelado'] ? 1 : 0.4}
                      onClick={() => toggleEstado('cancelado')}
                    >
                      {t('estado_cancelado')}
                    </Badge>
                    <Badge 
                      colorScheme="orange" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={mostrarInhabiles ? '2px solid white' : '2px solid transparent'}
                      opacity={mostrarInhabiles ? 1 : 0.4}
                      onClick={() => setMostrarInhabiles(!mostrarInhabiles)}
                    >
                      {t('admin_inhabiles_mostrar_inhabiles')}
                    </Badge>
                  </Flex>
                  
                  {/* Sub-estados en otra fila */}
                  <Flex gap={2} flexWrap="wrap">
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['bolsa'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['bolsa'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('bolsa')}
                    >
                      {t('subestado_bolsa')}
                    </Badge>
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['asignada_espera'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['asignada_espera'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('asignada_espera')}
                    >
                      {t('subestado_asignada_espera')}
                    </Badge>
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['en_curso'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['en_curso'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('en_curso')}
                    >
                      {t('subestado_en_curso')}
                    </Badge>
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['completado_sin_confirmar'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['completado_sin_confirmar'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('completado_sin_confirmar')}
                    >
                      {t('subestado_completado_sin_confirmar')}
                    </Badge>
                    <Badge 
                      colorScheme="blue" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['pendiente_devolucion'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['pendiente_devolucion'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('pendiente_devolucion')}
                    >
                      {t('subestado_pendiente_devolucion')}
                    </Badge>
                    <Badge 
                      colorScheme="yellow" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['pendiente_pago'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['pendiente_pago'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('pendiente_pago')}
                    >
                      {t('subestado_pendiente_pago')}
                    </Badge>
                    <Badge 
                      colorScheme="yellow" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['finalizado'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['finalizado'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('finalizado')}
                    >
                      {t('subestado_finalizado')}
                    </Badge>
                    <Badge 
                      colorScheme="yellow" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['fallido'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['fallido'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('fallido')}
                    >
                      {t('subestado_fallido')}
                    </Badge>
                    <Badge 
                      colorScheme="red" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['cancelado_cliente'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['cancelado_cliente'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('cancelado_cliente')}
                    >
                      {t('subestado_cancelado_cliente')}
                    </Badge>
                    <Badge 
                      colorScheme="red" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['cancelado_administrador'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['cancelado_administrador'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('cancelado_administrador')}
                    >
                      {t('subestado_cancelado_conductor')}
                    </Badge>
                    <Badge 
                      colorScheme="red" 
                      fontSize="0.75em" 
                      px={2} 
                      py={0.5} 
                      borderRadius="full"
                      cursor="pointer"
                      border={subEstadosActivos['cancelado_automatico'] ? '2px solid white' : '2px solid transparent'}
                      opacity={subEstadosActivos['cancelado_automatico'] ? 1 : 0.4}
                      onClick={() => toggleSubEstado('cancelado_automatico')}
                    >
                      {t('subestado_cancelado_automatico')}
                    </Badge>
                  </Flex>
                </Box>
              )}
            </Flex>

        <Grid templateColumns={expandedDay !== null ? "1fr" : "repeat(7, 1fr)"} gap="2px">
          {groupReservasByDay()
            .filter((day, index) => expandedDay === null || expandedDay === index)
            .map((day, mapIndex) => {
            const index = expandedDay !== null ? expandedDay : mapIndex;
            const dayReservas = day.reservas || [];
            const reservasFiltradas = dayReservas.filter(reserva => {
              const estadoActivo = estadosActivos[reserva.estado];
              const subEstadoActivo = subEstadosActivos[reserva.sub_estado];
              return estadoActivo && subEstadoActivo;
            });
            
            // Obtener periodos inhábiles para este día
            const formattedDate = new Intl.DateTimeFormat('es-ES', {
              timeZone: 'Europe/Madrid',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }).format(day.date);
            const horasInhabiles = inhabiles[formattedDate] || [];
            
            // Organizar reservas en carriles si el día está expandido
            const { reservasConCarril = [], totalCarriles = 1 } = expandedDay === index 
              ? organizarReservasEnCarriles(reservasFiltradas)
              : { reservasConCarril: reservasFiltradas.map(r => ({ ...r, carril: 0 })), totalCarriles: 1 };
            
            // Calcular posición y altura para cada reserva
            const calcularPosicionYAltura = (reserva) => {
              const inicio = new Date(reserva.fecha_hora_inicio);
              const fin = new Date(reserva.fecha_hora_fin);
              
              // Hora de inicio en decimal (ej: 14.5 para 14:30)
              const horaInicio = inicio.getHours() + inicio.getMinutes() / 60;
              // Duración en horas
              const duracion = (fin - inicio) / (1000 * 60 * 60);
              
              // Altura del día: 420px para 24 horas = 17.5px por hora
              const pixelsPorHora = 17.5;
              const top = horaInicio * pixelsPorHora;
              const altura = duracion * pixelsPorHora;
              
              return { top, altura };
            };
            
            return (
              <GridItem key={index}>
                <Box
                  bg="gray.800"
                  borderRadius="md"
                  p={3}
                  position="relative"
                  cursor="pointer"
                  onClick={() => {
                    if (expandedDay === null) {
                      setExpandedDay(index);
                    } else if (expandedDay === index) {
                      setExpandedDay(null);
                    }
                  }}
                  _hover={{ bg: "gray.700" }}
                >
                  {/* Encabezado del día */}
                  <Box mb={2}>
                    <HStack justify="space-between">
                      <Box flex="1">
                        <Text fontWeight="bold" fontSize="sm" textAlign="center" color="white">
                          {getDayName(index)}
                        </Text>
                        <Text fontSize="xs" textAlign="center" color="gray.400">
                          {day.date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                        </Text>
                      </Box>
                      {expandedDay === index && (
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedDay(null);
                          }}
                        >
                          ✕
                        </Button>
                      )}
                    </HStack>
                  </Box>
                  <Divider mb={2} />
                  
                  {/* Contenedor de 24 horas con altura fija */}
                  <Box
                    position="relative"
                    height="420px"
                    bg="gray.900"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    {/* Líneas de guía para las horas */}
                    {Array.from({ length: 24 }, (_, i) => (
                      <Box
                        key={i}
                        position="absolute"
                        top={`${i * 17.5}px`}
                        left="0"
                        right="0"
                        height="17.5px"
                        borderTop="1px solid"
                        borderColor="gray.700"
                      >
                        <Text fontSize="9px" color="gray.600" pl={1}>
                          {i}:00
                        </Text>
                      </Box>
                    ))}
                    
                    {/* Línea roja de la hora actual - solo mostrar si es el día de hoy */}
                    {(() => {
                      const today = new Date();
                      const dayDate = day.date;
                      const isToday = dayDate.getFullYear() === today.getFullYear() &&
                                      dayDate.getMonth() === today.getMonth() &&
                                      dayDate.getDate() === today.getDate();
                      
                      if (isToday) {
                        const horaActual = currentTime.getHours() + currentTime.getMinutes() / 60;
                        const topPosition = horaActual * 17.5;
                        
                        return (
                          <Box
                            position="absolute"
                            top={`${topPosition}px`}
                            left="0"
                            right="0"
                            height="2px"
                            bg="red.500"
                            zIndex="10"
                            boxShadow="0 0 4px rgba(255, 0, 0, 0.5)"
                          >
                            <Box
                              position="absolute"
                              left="0"
                              top="-4px"
                              width="8px"
                              height="8px"
                              borderRadius="50%"
                              bg="red.500"
                              boxShadow="0 0 4px rgba(255, 0, 0, 0.5)"
                            />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Periodos inhábiles */}
                    {mostrarInhabiles && horasInhabiles.map((hora) => (
                      <Box
                        key={`inhabil-${hora}`}
                        position="absolute"
                        top={`${hora * 17.5}px`}
                        left="2px"
                        right="2px"
                        height="17.5px"
                        bg="orange.800"
                        opacity="0.7"
                        borderRadius="sm"
                        borderLeft="4px solid"
                        borderLeftColor="orange.500"
                        zIndex="0"
                      />
                    ))}
                    
                    {/* Reservas posicionadas según su hora */}
                    {reservasConCarril.length === 0 && (!mostrarInhabiles || horasInhabiles.length === 0) ? (
                      <Text fontSize="xs" color="gray.500" textAlign="center" position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
                        {t('admin_inhabiles_sin_reservas')}
                      </Text>
                    ) : (
                      reservasConCarril.map((reserva) => {
                        const { top, altura } = calcularPosicionYAltura(reserva);
                        const carrilWidth = expandedDay === index ? `${95 / totalCarriles}%` : "calc(100% - 4px)";
                        const carrilLeft = expandedDay === index ? `calc(${(reserva.carril * 95) / totalCarriles}% + 2px)` : "2px";
                        
                        return (
                          <Tooltip
                            key={reserva.id}
                            label={
                              <Box className="tooltip-content">
                                <Text className="tooltip-title">Reserva #{reserva.id?.substring(0, 8)}</Text>
                                <VStack className="tooltip-fields" align="start" spacing={1}>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Cliente:</Text>
                                    <Text className="tooltip-field-value">{reserva.cliente_nombre} {reserva.cliente_apellidos}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Email:</Text>
                                    <Text className="tooltip-field-value">{reserva.cliente_email}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Teléfono:</Text>
                                    <Text className="tooltip-field-value">{reserva.cliente_telefono}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Conductor:</Text>
                                    <Text className="tooltip-field-value">
                                      {reserva.conductor_nombre 
                                        ? `${reserva.conductor_nombre} ${reserva.conductor_apellidos || ''}`.trim()
                                        : 'Sin asignar'}
                                    </Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Inicio:</Text>
                                    <Text className="tooltip-field-value">{new Date(reserva.fecha_hora_inicio).toLocaleString('es-ES')}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Fin:</Text>
                                    <Text className="tooltip-field-value">{new Date(reserva.fecha_hora_fin).toLocaleString('es-ES')}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Origen:</Text>
                                    <Text className="tooltip-field-value">{reserva.origen_direccion || reserva.origen_codigo_postal || 'N/A'}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Destino:</Text>
                                    <Text className="tooltip-field-value">{reserva.destino_direccion || reserva.destino_codigo_postal || 'N/A'}</Text>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Estado:</Text>
                                    <Badge colorScheme={getColorForEstado(reserva.estado)}>
                                      {t(`estado_${reserva.estado}`)}
                                    </Badge>
                                  </Box>
                                  <Box className="tooltip-field">
                                    <Text className="tooltip-field-label">Sub-estado:</Text>
                                    <Badge colorScheme={getColorForSubEstado(reserva.sub_estado)}>
                                      {t(`subestado_${reserva.sub_estado}`)}
                                    </Badge>
                                  </Box>
                                  {reserva.precio && (
                                    <Box className="tooltip-field">
                                      <Text className="tooltip-field-label">Precio:</Text>
                                      <Text className="tooltip-field-value">{reserva.precio}€</Text>
                                    </Box>
                                  )}
                                  {reserva.pasajeros && (
                                    <Box className="tooltip-field">
                                      <Text className="tooltip-field-label">Pasajeros:</Text>
                                      <Text className="tooltip-field-value">{reserva.pasajeros}</Text>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            }
                            placement="auto"
                            hasArrow
                            borderRadius="0.75rem"
                            bg="transparent"
                          >
                            <Box
                              position="absolute"
                              top={`${top}px`}
                              left={carrilLeft}
                              width={carrilWidth}
                              height={`${Math.max(altura, 20)}px`}
                              bg="gray.700"
                              borderRadius="md"
                              p={1}
                              fontSize="xs"
                              borderLeft="4px solid"
                              borderLeftColor={`${getColorForSubEstado(reserva.sub_estado)}.400`}
                              overflow="hidden"
                              zIndex="1"
                              cursor="pointer"
                              _hover={{ bg: "gray.600" }}
                            >
                              <HStack spacing={1} wrap="wrap" align="center">
                                <Text fontWeight="bold" fontSize="9px" whiteSpace="nowrap">
                                  {new Date(reserva.fecha_hora_inicio).toLocaleTimeString('es-ES', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })} - {new Date(reserva.fecha_hora_fin).toLocaleTimeString('es-ES', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </Text>
                                <Badge 
                                  colorScheme={getColorForEstado(reserva.estado)} 
                                  fontSize="7px"
                                >
                                  {t(`estado_${reserva.estado}`)}
                                </Badge>
                                <Badge 
                                  colorScheme={getColorForSubEstado(reserva.sub_estado)} 
                                  fontSize="7px"
                                >
                                  {t(`subestado_${reserva.sub_estado}`)}
                                </Badge>
                              </HStack>
                              {altura >= 40 && (
                                <>
                                  <Text color="gray.300" fontSize="9px" noOfLines={1} mt={0.5}>
                                    {reserva.cliente_email || 'Cliente'}
                                  </Text>
                                  <Text color="gray.400" fontSize="8px" noOfLines={1}>
                                    #{reserva.id.substring(0, 8)}
                                  </Text>
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })
                    )}
                  </Box>
                </Box>
              </GridItem>
            );
          })}
        </Grid>
          </CardBody>
        </Collapse>
      </Card>

      {/* JSON del mes actual */}
      {showJsonSection && (
        <Box bg="gray.800" color="white" borderRadius="md" p={4}>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Horas inhábiles para el mes actual
          </Text>
          <Box
            bg="gray.900"
            borderRadius="md"
            p={4}
            overflow="auto"
            maxH="300px"
            fontSize="sm"
          >
            <pre>{JSON.stringify(currentMonthData, null, 2)}</pre>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminInhabiles;